<template>
  <div class="w-full max-w-[460px] auth-page">
    <div class="flex justify-center mb-8">
      <AppLogo />
    </div>
    <!-- Solid surface, hairline border — refined look. -->
    <div class="relative overflow-hidden border border-white/[0.08] bg-[var(--surface-color)] rounded-lg">
      <div class="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-50" />

      <div class="p-8">
        <!-- Success State -->
        <div v-if="sent" class="text-center">
          <div class="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/[0.08] border border-primary/25 flex items-center justify-center">
            <NuxtIcon name="mdi:email-check-outline" class="text-3xl text-primary" />
          </div>
          <h1 class="font-heading text-xl font-bold text-white mb-2">{{ $t('auth.check_email') }}</h1>
          <p class="text-text-muted text-sm mb-6 leading-relaxed">
            {{ $t('auth.check_email_description') }}
          </p>
          <NuxtLink :to="localePath('/auth')" class="text-primary text-sm no-underline hover:text-white transition-colors">
            {{ $t('auth.back_to_login') }}
          </NuxtLink>
        </div>

        <!-- Form State -->
        <div v-else>
          <div class="text-center mb-6">
            <h1 class="font-heading text-xl font-bold text-white mb-1">{{ $t('auth.forgot_password') }}</h1>
            <p class="text-text-muted text-sm">{{ $t('auth.forgot_password_description') }}</p>
          </div>

          <form @submit.prevent="handleSubmit" class="space-y-3">
            <AppInput
              v-model="email"
              :label="$t('email')"
              type="email"
              placeholder="producer@example.com"
              autocomplete="email"
              name="email"
              size="lg"
            />

            <p v-if="error" class="text-rose-400 bg-rose-400/[0.08] p-2 rounded-lg border border-rose-400/25 text-sm text-center">{{ error }}</p>

            <div class="auth-submit">
              <AppButton
                variant="primary"
                native-type="submit"
                :loading="loading"
                size="lg"
              >
                {{ loading ? $t('auth.sending') : $t('auth.send_reset_link') }}
              </AppButton>
            </div>
          </form>

          <p class="mt-6 text-center">
            <NuxtLink :to="localePath('/auth')" class="text-text-muted text-sm no-underline hover:text-primary transition-colors">
              {{ $t('auth.back_to_login') }}
            </NuxtLink>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
definePageMeta({ layout: 'auth' })

const localePath = useLocalePath()

const email = ref('')
const loading = ref(false)
const error = ref('')
const sent = ref(false)

const handleSubmit = async () => {
  if (!email.value) return
  loading.value = true
  error.value = ''

  try {
    await $fetch('/api/auth/forgot-password', {
      method: 'POST',
      body: { email: email.value }
    })
    sent.value = true
  } catch (e) {
    error.value = e.data?.statusMessage || 'Something went wrong'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
/* See auth.vue — AppButton's inner el-button is inline-flex by default, so
   stretching the CTA to fill the form needs a deep selector on the el-button. */
.auth-submit :deep(.el-button) {
  width: 100%;
}
</style>
